<template>
  <Layout>
    <Teleport to="body">
      <div
        v-if="showBlockedModal"
        class="modal fade show login-page-modal"
        style="display: block"
      >
        <div class="modal-dialog modal-dialog-centered login-modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ t('blockedTitle') }}</h5>
              <button
                type="button"
                class="btn-close"
                :aria-label="t('common.close')"
                @click="closeBlockedModal"
              />
            </div>
            <div class="modal-body">
              {{ t('blockedBody') }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showBlockedModal"
        class="modal-backdrop fade show"
      ></div>
    </Teleport>

    <template v-if="$route.query.ext !== undefined">
      <div class="login-page__title">{{ t('externalSignIn') }}</div>
      <div class="login-page__form">
        <External />
      </div>

      <div class="login-page__back">
        <RouterLink
          :to="{ name: 'login' }"
          class="btn btn-link"
        >
          <i class="bi bi-arrow-left-circle me-2"></i>
          {{ t('common.back') }}
        </RouterLink>
      </div>
    </template>
    <template v-else>
      <div class="login-page__status">
        <div class="login-page__status-dot"></div>
        <span class="login-page__status-text">{{ t('systemAvailable') }}</span>
      </div>

      <div class="login-page__title">{{ t('chooseAccount') }}</div>

      <a
        href="#"
        class="login-page__option"
        @click.prevent="startEmployeeLogin"
      >
        <div class="login-page__option-icon login-page__option-icon--microsoft">
          <i class="bi bi-microsoft"></i>
        </div>
        <div class="login-page__option-body">
          <div class="login-page__option-title">{{ t('employeeAstrazeneca') }}</div>
          <small class="login-page__option-hint">{{ t('employeeHint') }}</small>
        </div>
      </a>

      <RouterLink
        :to="{ name: 'login', query: { ext: 1 } }"
        class="login-page__option"
      >
        <div class="login-page__option-icon login-page__option-icon--external">
          <i class="bi bi-person-badge"></i>
        </div>
        <div class="login-page__option-body">
          <div class="login-page__option-title">{{ t('externalUser') }}</div>
          <small class="login-page__option-hint">{{ t('externalHint') }}</small>
        </div>
      </RouterLink>
    </template>
  </Layout>
</template>

<style scoped>
.login-modal-dialog {
  max-width: 500px;
  width: 95%;
  margin-right: auto;
  margin-left: auto;
}
</style>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCookies } from '@vueuse/integrations/useCookies'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from 'soykajs/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import { useUiStore } from '@/stores/ui'
import config from '@/config'
import Layout from './login/layout.vue'
import External from './login/external.vue'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const { returnUrl } = storeToRefs(useUiStore())
const { push: openModal } = useMessageStack()

const { model: sso, refresh: getSso } = useApi({
  url: '/web/login',
})
const { set: setCookue } = useCookies(['service'])

const showBlockedModal = ref(false)
let loginErrorHandled = false
const employeeLoginPending = ref(false)

/**
 * Проверяет, что ошибка входа означает заблокированного пользователя.
 *
 * @param {unknown} raw сырое значение ошибки.
 * @returns {boolean} true, если доступ запрещён / пользователь неактивен.
 */
function isBlockedLoginError(raw) {
  const s = String(raw ?? '').toLowerCase()
  return s.includes('forbidden') || s.includes('not active') || s.includes('inactive') || s.includes('неактив')
}

/**
 * Закрывает модалку о блокировке доступа.
 *
 * @returns {void}
 */
function closeBlockedModal() {
  showBlockedModal.value = false
}

/**
 * Показывает ошибку входа (модалка или toast) и убирает login_error из query.
 *
 * @param {unknown} raw сырое значение из route.query.login_error.
 * @returns {Promise<void>}
 */
async function consumeLoginError(raw) {
  if (loginErrorHandled || raw == null || raw === '') {
    return
  }
  loginErrorHandled = true
  const loginError = Array.isArray(raw) ? raw[0] : raw
  if (isBlockedLoginError(loginError)) {
    showBlockedModal.value = true
  } else {
    // Toast: Login в app.vue монтируется до готовности route.query после SSO-редиректа.
    await openModal({ type: 'error', message: String(loginError) })
  }
  const nextQuery = { ...route.query }
  delete nextQuery.login_error
  await router.replace({ name: 'login', query: nextQuery })
}

watch(
  () => route.query.login_error,
  raw => {
    consumeLoginError(raw)
  },
  { immediate: true },
)

/**
 * Пишет cookie service=ltms для SSO / внешнего входа.
 *
 * @returns {void}
 */
function setServiceCookie() {
  setCookue('service', 'ltms', {
    domain: config.VITE_PRIMARY_DOMAIN,
    path: '/',
    sameSite: 'lax',
  })
}

/**
 * Собирает query для старта SSO (return URL после логина).
 *
 * @returns {object} объект query или пустой.
 */
function ssoStartQuery() {
  const ru = returnUrl.value
  if (ru && typeof ru === 'object' && ru.fullPath && !['login', 'sso_login'].includes(ru.name)) {
    return { next: ru.fullPath }
  }
  return {}
}

/**
 * Запускает Microsoft SSO: cookie, /web/login, редирект на auth_uri.
 *
 * @returns {Promise<void>}
 */
async function startEmployeeLogin() {
  if (employeeLoginPending.value) {
    return
  }
  employeeLoginPending.value = true
  try {
    setServiceCookie()
    const q = ssoStartQuery()
    await getSso(Object.keys(q).length ? { query: q } : {})
    const uri = sso.value?.auth_uri
    if (uri) {
      window.location.href = uri
    } else {
      await openModal({ type: 'error', message: t('ssoStartFailed') })
    }
  } catch {
    await openModal({ type: 'error', message: t('ssoStartFailed') })
  } finally {
    employeeLoginPending.value = false
  }
}

onMounted(async () => {
  setServiceCookie()
  const q = ssoStartQuery()
  await getSso(Object.keys(q).length ? { query: q } : {})
})
</script>

<i18n>
{
  "ru": {
    "chooseAccount": "Выберите способ входа",
    "systemAvailable": "Система доступна",
    "employeeAstrazeneca": "Сотрудник АстраЗенека",
    "employeeHint": "Вход через Microsoft SSO",
    "externalUser": "Внешний пользователь",
    "externalHint": "Логин и пароль",
    "externalSignIn": "Вход по логину и паролю",
    "blockedTitle": "Доступ заблокирован",
    "blockedBody": "Доступ заблокирован. Обратитесь к администратору",
    "ssoStartFailed": "Не удалось начать вход через Microsoft. Повторите попытку."
  },
  "en": {
    "chooseAccount": "Choose sign-in method",
    "systemAvailable": "System available",
    "employeeAstrazeneca": "AstraZeneca employee",
    "employeeHint": "Sign in with Microsoft SSO",
    "externalUser": "External user",
    "externalHint": "Username and password",
    "externalSignIn": "Sign in with username and password",
    "blockedTitle": "Access blocked",
    "blockedBody": "Access is blocked. Please contact an administrator.",
    "ssoStartFailed": "Could not start Microsoft sign-in. Please try again."
  }
}
</i18n>
